import { ApiError, NetworkError, ProductionGuardError, isAbortError, parseErrorEnvelope } from './errors';
import { readRateLimit, type RateLimitInfo } from './normalize/rateLimit';
import type {
  BookingFormConfig,
  BookingPolicy,
  CreateBookingRequest,
  CreateBookingResponse,
  CustomField,
  EstimatePriceRequest,
  Frequency,
  GiftcardDiscountRequest,
  GiftcardDiscountResponse,
  GiftcardPurchaseRequest,
  GiftcardPurchaseResponse,
  LocationPolicy,
  LocationRequest,
  LocationResponse,
  PriceEstimate,
  Service,
  SetupIntentRequest,
  SetupIntentResponse,
  SpotsRequest,
  SpotsResponse,
  TenantSettings,
} from './types';

export type Env = 'sandbox' | 'production';

export interface RetryPolicy {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  jitter: boolean;
}

export interface ApiClientConfig {
  /** e.g. https://chasedetailing-sandbox.launch27.com/v1 — tenant is the subdomain, never hardcoded */
  baseUrl: string;
  env: Env;
  /** Required in addition to env === 'production' before any write is allowed. */
  allowProduction?: boolean;
  /** Injectable transport: mock mode and tests. Defaults to global fetch. */
  fetch?: typeof fetch;
  retry?: Partial<RetryPolicy>;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
  onRateLimit?: (info: RateLimitInfo) => void;
  /**
   * Supplies the customer bearer for the portal bundle. Returning null sends no Authorization
   * header at all, so the booking and gift-card bundles — which never set this — are unaffected.
   */
  getToken?: () => string | null;
}

/**
 * 'safe' retries network errors too; 'rate-limit-only' never re-sends a possibly-applied write
 * but still retries a 429; 'none' never retries anything.
 *
 * 'none' exists for /login: the account locks after 5 failed attempts in a minute, so an
 * automatic retry burns the customer's budget and can lock an account that had one typo.
 */
export type RetryMode = 'safe' | 'rate-limit-only' | 'none';

export interface RequestOptions {
  // PUT is used only by the portal's reschedule. Verified 2026-10-05 that the tenant's preflight
  // allows it with `authorization,content-type`, the order a browser produces.
  method: 'GET' | 'POST' | 'PUT';
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  retry: RetryMode;
  /**
   * 'no-store' keeps customer data out of the shared-machine disk cache. Only 'no-store' is
   * safe here: 'no-cache' and 'reload' make the UA append Cache-Control/Pragma request headers,
   * neither CORS-safelisted, which would change the preflight string (see the note in request()).
   */
  cache?: 'no-store';
}

const DEFAULT_RETRY: RetryPolicy = { maxAttempts: 3, baseDelayMs: 500, maxDelayMs: 8000, jitter: true };
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);
export const SANDBOX_HOST_PATTERN = /-sandbox\.(launch27\.com|l27\.co)$/i;

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function createApiClient(config: ApiClientConfig) {
  const baseUrl = config.baseUrl.replace(/\/+$/, '');
  const host = new URL(baseUrl).host;
  const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
  const retry: RetryPolicy = { ...DEFAULT_RETRY, ...config.retry };
  const sleep = config.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const random = config.random ?? Math.random;
  let lastRateLimit: RateLimitInfo | null = null;

  if (config.env === 'sandbox' && !SANDBOX_HOST_PATTERN.test(host)) {
    throw new ProductionGuardError(
      `env is "sandbox" but the API host "${host}" is not a sandbox tenant. Refusing to start.`,
    );
  }

  function backoff(attempt: number): number {
    const exp = Math.min(retry.maxDelayMs, retry.baseDelayMs * 2 ** (attempt - 1));
    return retry.jitter ? Math.round(exp * (0.5 + random())) : exp;
  }

  function buildUrl(path: string, query?: RequestOptions['query']): string {
    const url = new URL(baseUrl + path);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }

  function guardWrite(what: string): void {
    if (config.env === 'production' && !config.allowProduction) {
      throw new ProductionGuardError(
        `${what} against production is blocked: set allowProduction to opt in explicitly.`,
      );
    }
  }

  async function request<T>(opts: RequestOptions): Promise<T> {
    const url = buildUrl(opts.path, opts.query);
    for (let attempt = 1; ; attempt++) {
      let res: Response;
      try {
        // Exactly two headers are ever sent. The tenant's CORS preflight allowlist is ordered
        // (`accept, authorization, content-type`); browsers sort Access-Control-Request-Headers
        // alphabetically, so `authorization,content-type` always matches. Any additional custom
        // header changes that string and the preflight is rejected outright. Do not add one.
        const headers: Record<string, string> = {};
        if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
        const token = config.getToken?.() ?? null;
        if (token) headers.Authorization = `Bearer ${token}`;

        res = await fetchImpl(url, {
          method: opts.method,
          headers: Object.keys(headers).length > 0 ? headers : undefined,
          body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
          signal: opts.signal,
          ...(opts.cache ? { cache: opts.cache } : {}),
        });
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (opts.retry === 'safe' && attempt < retry.maxAttempts) {
          await sleep(backoff(attempt));
          continue;
        }
        throw new NetworkError(`Network error calling ${opts.method} ${opts.path}`, err);
      }

      const rl = readRateLimit(res.headers);
      lastRateLimit = rl;
      if (config.onRateLimit && (rl.limit !== null || rl.remaining !== null)) config.onRateLimit(rl);

      const body = await readBody(res);
      if (res.ok) return body as T;

      const { message, fieldErrors } = parseErrorEnvelope(res.status, res.statusText, body);
      const error = new ApiError(res.status, message, fieldErrors, body, rl.retryAfterMs);
      const retryable =
        opts.retry !== 'none' &&
        RETRYABLE_STATUS.has(res.status) &&
        (opts.retry === 'safe' || res.status === 429) &&
        attempt < retry.maxAttempts;
      if (!retryable) throw error;
      await sleep(rl.retryAfterMs ?? backoff(attempt));
    }
  }

  const get = <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>({ method: 'GET', path, query, signal, retry: 'safe' });

  return {
    baseUrl,
    host,
    env: config.env,
    get lastRateLimit(): RateLimitInfo | null {
      return lastRateLimit;
    },

    /**
     * Escape hatch for endpoint groups that live outside this object — currently the customer
     * portal (src/api/portal.ts), which is kept separate so its methods stay out of the booking
     * and gift-card bundles. Carries the retry, backoff and error-envelope handling above.
     */
    request,
    guardWrite,

    getSettings: (signal?: AbortSignal) => get<TenantSettings>('/settings', undefined, signal),
    getBookingForm: (type: string, signal?: AbortSignal) =>
      get<BookingFormConfig>('/booking/form', { type }, signal),
    getServices: (signal?: AbortSignal) => get<Service[]>('/booking/services', undefined, signal),
    getFrequencies: (signal?: AbortSignal) => get<Frequency[]>('/booking/frequencies', undefined, signal),
    getCustomFields: (signal?: AbortSignal) =>
      get<CustomField[]>('/booking/custom_fields', undefined, signal),
    getBookingPolicy: (signal?: AbortSignal) => get<BookingPolicy>('/policy/booking', undefined, signal),
    getLocationPolicy: (signal?: AbortSignal) =>
      get<LocationPolicy>('/policy/location', undefined, signal),

    resolveLocation: (body: LocationRequest, signal?: AbortSignal) =>
      request<LocationResponse>({ method: 'POST', path: '/booking/location', body, signal, retry: 'safe' }),
    getSpots: (body: SpotsRequest, signal?: AbortSignal) =>
      request<SpotsResponse>({ method: 'POST', path: '/booking/spots', body, signal, retry: 'safe' }),
    estimatePrice: (body: EstimatePriceRequest, signal?: AbortSignal) =>
      request<PriceEstimate>({ method: 'POST', path: '/booking/estimate_price', body, signal, retry: 'safe' }),

    createSetupIntent: (body: SetupIntentRequest, signal?: AbortSignal) => {
      guardWrite('Creating a Stripe SetupIntent');
      return request<SetupIntentResponse>({
        method: 'POST',
        path: '/setup/billing/setup_intent',
        body,
        signal,
        retry: 'rate-limit-only',
      });
    },
    /** Always the reCAPTCHA-protected variant. Never retried on network errors (may have applied). */
    createBooking: (body: CreateBookingRequest, signal?: AbortSignal) => {
      guardWrite('Creating a booking');
      return request<CreateBookingResponse>({
        method: 'POST',
        path: '/bookings',
        body,
        signal,
        retry: 'rate-limit-only',
      });
    },
    /** Non-mutating discount-code check for gift cards (percentage-based). */
    checkGiftcardDiscount: (body: GiftcardDiscountRequest, signal?: AbortSignal) =>
      request<GiftcardDiscountResponse>({ method: 'POST', path: '/giftcard/discount', body, signal, retry: 'safe' }),
    /**
     * Purchases a gift card — charges the card IMMEDIATELY via a Stripe PaymentIntent.
     * Never retried on network errors (the charge may have applied).
     */
    purchaseGiftcard: (body: GiftcardPurchaseRequest, signal?: AbortSignal) => {
      guardWrite('Purchasing a gift card');
      return request<GiftcardPurchaseResponse>({
        method: 'POST',
        path: '/giftcard',
        body,
        signal,
        retry: 'rate-limit-only',
      });
    },
    /**
     * The documented captcha-less variant (`POST /booking`) — what the vendor's own widget uses,
     * and the only endpoint that works when the tenant has the captcha disabled, since
     * `/bookings` rejects an empty token. See bookingEndpoint() in domain/captcha.ts.
     * Never retried on network errors (the booking may have been created).
     */
    createBookingNoCaptcha: (body: CreateBookingRequest, signal?: AbortSignal) => {
      guardWrite('Creating a booking');
      return request<CreateBookingResponse>({
        method: 'POST',
        path: '/booking',
        body,
        signal,
        retry: 'rate-limit-only',
      });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
